import { config } from '@/shared/constants';
import { storefrontClient } from './storeFrontClient';

export interface ShopifyImage {
  id: string;
  url: string;
  altText: string | null;
  width: number;
  height: number;
}

export interface ShopifyMediaImage {
  __typename: 'MediaImage';
  id: string;
  image: ShopifyImage;
}

export interface ShopifyMediaVideo {
  __typename: 'Video';
  id: string;
  sources: Array<{
    url: string;
    mimeType: string;
    format: string;
    height: number;
    width: number;
  }>;
  alt: string | null;
}

export type ShopifyMedia = ShopifyMediaImage | ShopifyMediaVideo;

export interface ShopifyVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice: {
    amount: string;
    currencyCode: string;
  } | null;
  availableForSale: boolean;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  image: ShopifyImage | null;
}

export interface ShopifyMetafield {
  key: string;
  value: string;
  namespace: string;
}

export interface ShopifyProduct {
  id: string;
  handle: string;
  title: string;
  description: string;
  descriptionHtml: string;
  productType: string;
  tags: string[];
  variants: ShopifyVariant[];
  images: ShopifyImage[];
  media: ShopifyMedia[];
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  metafields: ShopifyMetafield[];
}

export async function fetchShopifyProducts(): Promise<ShopifyProduct[]> {
  const query = `
    query getProducts @inContext(country:  ${config.countryCode}) {
      products(first: 50) {
        edges {
          node {
            id
            handle
            title
            description
            descriptionHtml
            productType
            tags
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            variants(first: 20) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
            images(first: 20) {
              edges {
                node {
                  id
                  url
                  altText
                  width
                  height
                }
              }
            }
            media(first: 20) {
              edges {
                node {
                  __typename
                  ... on MediaImage {
                    id
                    image {
                      id
                      url
                      altText
                      width
                      height
                    }
                  }
                  ... on Video {
                    id
                    alt
                    sources {
                      url
                      mimeType
                      format
                      height
                      width
                    }
                  }
                }
              }
            }
            metafields(identifiers: [
              { namespace: "custom", key: "tech_specs_size" },
              { namespace: "custom", key: "tech_specs_weight" },
              { namespace: "custom", key: "tech_specs_material" },
              { namespace: "custom", key: "tech_specs_chip" },
              { namespace: "custom", key: "tech_specs_compatibility" },
              { namespace: "custom", key: "how_it_works_description" },
              { namespace: "custom", key: "benefits" },
              { namespace: "custom", key: "hero_video_mobile" },
              { namespace: "custom", key: "hero_video_desktop" },
              { namespace: "custom", key: "hero_video_mobile_fallback" },
              { namespace: "custom", key: "hero_video_desktop_fallback" },
              { namespace: "custom", key: "hero_main_heading" },
              { namespace: "custom", key: "hero_sub_heading" },
              { namespace: "custom", key: "hero_description" },
              { namespace: "custom", key: "quantity_discounts" }
            ]) {
              key
              value
              namespace
            }
          }
        }
      }
    }
  `;

  try {
    const response = await storefrontClient.post('', { query });

    if (response.data.errors) {
      console.error('Shopify GraphQL errors:', response.data.errors);
      throw new Error('Failed to fetch products: ' + JSON.stringify(response.data.errors));
    }

    const edges = response.data.data.products.edges;

    return edges.map(({ node }: any) => ({
      id: node.id,
      handle: node.handle,
      title: node.title,
      description: node.description,
      descriptionHtml: node.descriptionHtml,
      productType: node.productType,
      tags: node.tags,
      priceRange: node.priceRange,
      variants: node.variants.edges.map(({ node: variant }: any) => ({
        id: variant.id,
        title: variant.title,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        availableForSale: variant.availableForSale,
        selectedOptions: variant.selectedOptions,
        image: variant.image,
      })),
      images: node.images.edges.map(({ node: image }: any) => ({
        id: image.id,
        url: image.url,
        altText: image.altText,
        width: image.width,
        height: image.height,
      })),
      media: node.media.edges
        .map(({ node: media }: any) => {
          if (media.__typename === 'MediaImage') {
            return {
              __typename: 'MediaImage',
              id: media.id,
              image: media.image,
            };
          } else if (media.__typename === 'Video') {
            return {
              __typename: 'Video',
              id: media.id,
              sources: media.sources,
              alt: media.alt,
            };
          }
          return null;
        })
        .filter((m: any) => m !== null),
      metafields: node.metafields.filter((m: any) => m !== null),
    }));
  } catch (error) {
    console.error('Error fetching Shopify products:', error);
    throw error;
  }
}

export async function fetchShopifyProduct(handle: string): Promise<ShopifyProduct | null> {
  const query = `
    query getProduct($handle: String!) @inContext(country: ${config.countryCode}) {
      product(handle: $handle) {
        id
        handle
        title
        description
        descriptionHtml
        productType
        tags
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 20) {
          edges {
            node {
              id
              title
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              availableForSale
              selectedOptions {
                name
                value
              }
              image {
                id
                url
                altText
                width
                height
              }
            }
          }
        }
        images(first: 20) {
          edges {
            node {
              id
              url
              altText
              width
              height
            }
          }
        }
        media(first: 20) {
          edges {
            node {
              __typename
              ... on MediaImage {
                id
                image {
                  id
                  url
                  altText
                  width
                  height
                }
              }
              ... on Video {
                id
                alt
                sources {
                  url
                  mimeType
                  format
                  height
                  width
                }
              }
            }
          }
        }
        metafields(identifiers: [
          { namespace: "custom", key: "tech_specs_size" },
          { namespace: "custom", key: "tech_specs_weight" },
          { namespace: "custom", key: "tech_specs_material" },
          { namespace: "custom", key: "tech_specs_chip" },
          { namespace: "custom", key: "tech_specs_compatibility" },
          { namespace: "custom", key: "how_it_works_description" },
          { namespace: "custom", key: "benefits" },
          { namespace: "custom", key: "hero_video_mobile" },
          { namespace: "custom", key: "hero_video_desktop" },
          { namespace: "custom", key: "hero_video_mobile_fallback" },
          { namespace: "custom", key: "hero_video_desktop_fallback" },
          { namespace: "custom", key: "hero_main_heading" },
          { namespace: "custom", key: "hero_sub_heading" },
          { namespace: "custom", key: "hero_description" },
          { namespace: "custom", key: "quantity_discounts" }
        ]) {
          key
          value
          namespace
        }
      }
    }
  `;

  try {
    const response = await storefrontClient.post('', {
      query,
      variables: { handle },
    });

    if (response.data.errors) {
      console.error('Shopify GraphQL errors:', response.data.errors);
      return null;
    }

    const product = response.data.data.product;
    if (!product) return null;

    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      description: product.description,
      descriptionHtml: product.descriptionHtml,
      productType: product.productType,
      tags: product.tags,
      priceRange: product.priceRange,
      variants: product.variants.edges.map(({ node: variant }: any) => ({
        id: variant.id,
        title: variant.title,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        availableForSale: variant.availableForSale,
        selectedOptions: variant.selectedOptions,
        image: variant.image,
      })),
      images: product.images.edges.map(({ node: image }: any) => ({
        id: image.id,
        url: image.url,
        altText: image.altText,
        width: image.width,
        height: image.height,
      })),
      media: product.media.edges
        .map(({ node: media }: any) => {
          if (media.__typename === 'MediaImage') {
            return {
              __typename: 'MediaImage',
              id: media.id,
              image: media.image,
            };
          } else if (media.__typename === 'Video') {
            return {
              __typename: 'Video',
              id: media.id,
              sources: media.sources,
              alt: media.alt,
            };
          }
          return null;
        })
        .filter((m: any) => m !== null),
      metafields: product.metafields.filter((m: any) => m !== null),
    };
  } catch (error) {
    console.error('Error fetching Shopify product:', error);
    return null;
  }
}
